import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Pulse, Warning, Sparkle, X } from '@phosphor-icons/react';

const BANDS = { critical: 'rose', high: 'orange', medium: 'amber', low: 'green' };
const Badge = ({ children, tone = 'slate' }) => (
  <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-${tone}-100 text-${tone}-700`}>{children}</span>
);

const NarrativeBox = ({ text }) => {
  if (!text) return null;
  const [summary, ...rest] = text.split('INTERVENTIONS:');
  return (
    <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-3 text-sm whitespace-pre-line" data-testid="risk-narrative">
      <p>{summary.replace('SUMMARY:', '').trim()}</p>
      {rest.length > 0 && <div className="mt-2"><p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Suggested interventions</p><p className="text-slate-700">{rest.join('').trim()}</p></div>}
    </div>
  );
};

const DetailDialog = ({ sid, onClose }) => {
  const qc = useQueryClient();
  const { data: d } = useQuery({ queryKey: ['risk-detail', sid], queryFn: () => api.get(`/predict/dropout-risk/${sid}`).then((r) => r.data) });
  const gen = useMutation({
    mutationFn: () => api.post(`/predict/dropout-risk/${sid}/narrative`).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['risk-detail', sid] }); toast.success('Narrative generated'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="risk-detail-dialog">
      <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold">{d?.name || 'Student'}</h3>
          <button onClick={onClose} data-testid="risk-detail-close"><X /></button>
        </div>
        {!d ? <p className="text-sm text-slate-400">Loading...</p> : (
          <>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <Badge tone={BANDS[d.band]}>{d.band} risk</Badge>
              <span className="font-display text-3xl font-extrabold">{d.score}<span className="text-sm text-slate-400">/100</span></span>
              <span className="text-xs text-slate-400">Grade {d.grade}{d.section}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-white border border-slate-200 rounded p-2"><p className="text-[9px] uppercase text-slate-400">Attendance</p><p className="font-bold">{d.attendance_pct != null ? `${d.attendance_pct}%` : 'n/a'}</p></div>
              <div className="bg-white border border-slate-200 rounded p-2"><p className="text-[9px] uppercase text-slate-400">Avg marks</p><p className="font-bold">{d.avg_marks != null ? `${d.avg_marks}%` : 'n/a'}</p></div>
              <div className="bg-white border border-slate-200 rounded p-2"><p className="text-[9px] uppercase text-slate-400">Arrears</p><p className="font-bold">{d.arrears > 0 ? d.arrears.toLocaleString() : '0'}</p></div>
            </div>
            <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Risk factors</p>
            {d.factors.map((f, i) => (
              <div key={i} className="flex items-center gap-2 py-1 border-t border-slate-100 text-sm" data-testid={`risk-factor-${f.factor}`}>
                <Badge tone={f.severity === 'high' ? 'rose' : f.severity === 'medium' ? 'amber' : 'slate'}>{f.factor}</Badge>
                <span className="text-slate-600">{f.detail}</span>
                {f.points > 0 && <span className="ml-auto text-rose-600 font-mono text-xs">+{f.points}</span>}
              </div>
            ))}
            <NarrativeBox text={d.narrative} />
            <Button className="mt-3" onClick={() => gen.mutate()} disabled={gen.isPending} data-testid="generate-narrative-btn">
              <Sparkle className="mr-1" /> {gen.isPending ? 'Generating...' : d.narrative ? 'Regenerate AI narrative' : 'Generate AI narrative & interventions'}
            </Button>
            {d.narrative_generated_at && <p className="text-[10px] text-slate-400 mt-1">Last generated {d.narrative_generated_at.slice(0, 16).replace('T', ' ')}</p>}
          </>
        )}
      </div>
    </div>
  );
};

export default function Insights() {
  const [band, setBand] = useState('');
  const [openSid, setOpenSid] = useState(null);
  const { data } = useQuery({ queryKey: ['dropout-risk', band], queryFn: () => api.get(`/predict/dropout-risk${band ? `?band=${band}` : ''}`).then((r) => r.data) });
  const s = data?.summary;
  const kpis = s ? [['Critical', s.critical, 'rose'], ['High', s.high, 'orange'], ['Medium', s.medium, 'amber'], ['Low', s.low, 'green'], ['Avg score', s.avg_score, 'slate']] : [];
  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="insights-page">
      <div className="flex items-center gap-3 mb-1">
        <Pulse className="text-rose-600" size={26} />
        <h1 className="font-display text-2xl font-extrabold">Risk & Insights</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">Transparent dropout-risk scoring from attendance, marks and fee arrears - every point is explainable. Open a student for an AI-written summary and suggested interventions.</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {kpis.map(([label, n, tone]) => (
          <button key={label} onClick={() => setBand(label === 'Avg score' ? '' : (band === label.toLowerCase() ? '' : label.toLowerCase()))}
            className={`text-left bg-white border rounded-md p-3 card-hover ${band === label.toLowerCase() ? `border-${tone}-400 ring-1 ring-${tone}-300` : 'border-slate-200'}`}
            data-testid={`risk-kpi-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <p className="font-mono-label text-[9px] uppercase text-slate-400">{label}</p>
            <p className={`font-display text-2xl font-extrabold text-${tone}-600`}>{n}</p>
          </button>
        ))}
      </div>

      {band && <div className="mb-3 text-xs text-slate-500">Filtered to <Badge tone={BANDS[band]}>{band}</Badge> <button className="text-blue-600 ml-2" onClick={() => setBand('')}>clear</button></div>}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="risk-table">
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 px-4 py-2 bg-slate-50 text-[10px] uppercase text-slate-400 font-mono-label">
          <span>Student</span><span>Attendance</span><span>Marks</span><span>Arrears</span><span>Risk</span>
        </div>
        {!data ? <p className="text-sm text-slate-400 p-4">Scoring students...</p> :
          data.students.length === 0 ? <p className="text-sm text-slate-400 p-4" data-testid="risk-empty">No students match.</p> :
          data.students.map((r) => (
            <button key={r.student_id} onClick={() => setOpenSid(r.student_id)}
              className="w-full grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 px-4 py-2.5 border-t border-slate-100 text-sm items-center hover:bg-slate-50 text-left"
              data-testid={`risk-row-${r.student_id}`}>
              <span><span className="font-medium">{r.name}</span> <span className="text-xs text-slate-400">Gr {r.grade}{r.section}</span></span>
              <span className={r.attendance_pct != null && r.attendance_pct < 75 ? 'text-rose-600' : 'text-slate-500'}>{r.attendance_pct != null ? `${r.attendance_pct}%` : '-'}</span>
              <span className={r.avg_marks != null && r.avg_marks < 60 ? 'text-rose-600' : 'text-slate-500'}>{r.avg_marks != null ? `${r.avg_marks}%` : '-'}</span>
              <span className={r.arrears > 0 ? 'text-amber-600' : 'text-slate-400'}>{r.arrears > 0 ? r.arrears.toLocaleString() : '-'}</span>
              <span className="flex items-center gap-2 justify-end">
                {r.band === 'critical' && <Warning className="text-rose-600" />}
                <Badge tone={BANDS[r.band]}>{r.band}</Badge>
                <span className="font-display font-extrabold w-8 text-right">{r.score}</span>
              </span>
            </button>
          ))}
      </div>
      {data?.methodology && <p className="text-[11px] text-slate-400 mt-3">{data.methodology}</p>}
      {openSid && <DetailDialog sid={openSid} onClose={() => setOpenSid(null)} />}
    </div>
  );
}
